/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- guide payload and pcg-core are loose-typed by design */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/restrict-plus-operands --
 * The guide payload and pcg-core surface are loose-typed (.js + surface .d.ts)
 * by design (TS conversion is opportunistic). */

// PcgRecordingViewer — the React lifecycle owner for the Medaica Viewer's
// per-recording surface: the canvas layer, the
// transport/loop controls, the guide-layer controls, the Provisional Results
// bar and the opt-in research panel, laid out as the handoff's
// `.aptm-wave-main` column (v0.8.3). The exam-level chrome that the handoff's
// exam overlay injected around it — the selected-recording navigation,
// the listening-filter block, the Recording Review controls and the export
// actions — is supplied by the host page through the slot props below.
//
// The prototype had NO lifecycle boundary: bootstrap was bare top-level script,
// ~11 window/document listeners were never removed, a rAF loop and blob URLs
// leaked, the AudioContext was never closed, and createMediaElementSource made
// remounting the audio element throw. This component exists to own all of that:
//
//   - pcg-core context: rebuilt (useMemo) when payload / turbulent model
//     change — an input change REPLACES the
//     context (immutable-context pattern; kills the D._analysisQuality memo bug).
//   - timing engine (upstream v0.9.1 m1acq): a guide payload with `mvp2Timing`
//     gives the core a metric scope (the Evaluation Range: every result) and a
//     visual scope (the whole recording's markers: the canvas). With a ready
//     range the view is framed on it and playback starts at it, once per
//     recording, and the strip / Confidence row show it; a block that is not
//     ready shows upstream's "Analysis unavailable" state instead.
//   - canvases: refs, drawn imperatively via the verbatim vendored canvas modules.
//   - interactions: the vendored attach*() functions, every one paired with its
//     detach() in the effect cleanup; the Loop controller (canvas/loop.js) is
//     created once per render context and disposed with it.
//   - keyboard shortcuts: active only while the pointer is over the component
//     or focus is inside it (prototype: window-global, always on).
//   - AudioContext + createMediaElementSource: created once per mounted <audio>
//     element, closed on unmount (audio-graph.js).
//   - rAF playback loop: started on play, cancelled on pause/ended/unmount.
//   - blob URLs: created for Blob audioSrc, revoked on change/unmount.
//   - `view`/`state` stay mutable objects mutated by pan/zoom/loop at rAF
//     rates — deliberately NOT React state (that would re-render per mouse
//     move); the imperative redraw() is the prototype's own render schedule.
//     React owns the discrete chrome state (layer toggles, view mode, tabs)
//     and mirrors it into `state` before every draw.

import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"
import "./pcg-viewer.css"
import MedaicaLogo from "@medaica/common/components/medaica-logo"
import {
  createPcgCore,
  energySweepKey,
  ENGINE_READY,
  evaluationRangeEngine,
  evaluationRangeFrame,
  evaluationRangeStart,
} from "./core"
import { rejectGate, isGateHardRejected } from "./core/gate"
import { fmtNumber } from "./core/format"
import {
  createRenderContext,
  decodeCanvasTraces,
  createView,
  createViewerState,
  clampView,
  setZoomCentered,
  ensurePlaybackVisible,
  drawWave,
  drawSpec,
  attachWaveTooltip,
  attachWavePanZoom,
  attachLoopRuler,
  attachKeyboardShortcuts,
  createLoopController,
  CWT_BANDS,
} from "./canvas"
import { createAudioGraph } from "./audio-graph"
import { playbackFilterLevelGains } from "./audio/pcg-playback-filter"
import {
  DEFAULT_GAIN_SLIDER_VALUE,
  formatPlaybackGain,
  playbackGainFromSlider,
  speakerIconFor,
} from "./audio/playback-gain"
import PcgStatsBar from "./cards/stats-bar"
import PcgPatternResultsCard from "./cards/pattern-results-card"
import PcgTriagePanel from "./cards/triage-panel"
import PcgAuditPanel from "./cards/audit-panel"
import { PcgEvaluationRangeConfidence, PcgEvaluationRangeStrip } from "./cards/evaluation-range"
import PcgViewerHelpDialog, { PcgViewerAboutDialog } from "./PcgViewerHelpDialog"

export type PcgRecordingViewerProps = {
  /** Guide payload (the prototype's __DATA__ / `D`). */
  payload: any
  /** Serialized turbulent-energy model (the prototype's __TURBULENT_MODEL__). */
  turbulentModel?: any
  /** Audio source: URL string or Blob (blob URLs are revoked on unmount). */
  audioSrc?: string | Blob | null
  recordingId?: string | null
  /** Exam-level timing consistency bundle (exam overlay context). */
  examTimingConsistency?: any
  onExportPackage?: (pkg: any) => void
  /**
   * Coherent Energy (research-only): reports the currently selected research
   * sweep band (preselected strongest band on load, then whatever the inline
   * 200 Hz sweep chooses). Fired on mount and on every band change.
   */
  onEnergyBandChange?: (bandKey: string, meta: { autoSelected: boolean }) => void
  /**
   * Reports the wrapper-owned Web Audio graph (audio-graph.js) after mount and
   * null before unmount. The ONLY sanctioned way for a host page to reach the
   * page's single AudioContext — e.g. to splice the listening filter via
   * graph.spliceFilter (see use-pcg-playback-filter).
   */
  onAudioGraph?: (graph: any) => void
  /**
   * Reports the Tips On / Off preference after mount and on every change, so
   * host controls outside the viewer (the exam body map) show their tips
   * under the same switch.
   */
  onTooltipsEnabledChange?: (enabled: boolean) => void
  className?: string
  /**
   * Exam-overlay slots (handoff v0.8.3 `.aptm-wave-main` layout). Each is
   * optional; standalone use renders the per-recording surface alone.
   */
  /**
   * Where the brand (the Medaica logo and the "Viewer" menu button) renders.
   * By default it leads the "Selected Recording" head row; an exam host that
   * has its own top bar above the workspace passes that bar's element and
   * the brand is portalled there (its Quick Start / About dialogs stay here).
   */
  brandContainer?: HTMLElement | null
  /** Content of the "Selected Recording" head row, left of the Tips On / Off toggle. */
  recordingHeader?: ReactNode
  /** Site label of the selected recording (e.g. "Aortic"), named in the Provisional Results tip. */
  recordingLabel?: string
  /** Host controls at the right of the head row, between the Tips On / Off toggle and Quick Start. */
  recordingHeaderActions?: ReactNode
  /** The listening-filter block placed after the playback block in the Audio row. */
  filterControls?: ReactNode
  /** Left side of the Recording Review row (Accept / Reject / Note). */
  reviewControls?: ReactNode
  /** Right side of the Recording Review row, after the host's review controls. */
  secondaryActions?: ReactNode
  /**
   * Full-width lines under the Recording Review row's buttons (the handoff's
   * report-sources and note summaries): what the review has accepted so far,
   * the selected recording's note, the report's status.
   */
  reviewSummary?: ReactNode
}

// core-viewer.js formatAudioTime, verbatim.
function formatAudioTime(seconds: unknown): string {
  const s = Math.max(0, Number.isFinite(Number(seconds)) ? Number(seconds) : 0)
  const mins = Math.floor(s / 60)
  const secs = Math.floor(s % 60)
  return `${mins}:${String(secs).padStart(2, "0")}`
}

// Transport glyphs (handoff v0.8.3 transportIcon), verbatim paths.
const TransportIcon = ({ name }: { name: "start" | "play" | "pause" | "loop" }): ReactElement => {
  if (name === "start") {
    return (
      <svg className="transport-icon" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="4" y="4" width="3.5" height="16" rx="1.2" fill="currentColor" />
        <path d="M20 4.8 8.6 12 20 19.2Z" fill="currentColor" />
      </svg>
    )
  }
  if (name === "pause") {
    return (
      <svg className="transport-icon" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="5" y="4" width="5" height="16" rx="1.2" fill="currentColor" />
        <rect x="14" y="4" width="5" height="16" rx="1.2" fill="currentColor" />
      </svg>
    )
  }
  if (name === "loop") {
    return (
      <svg className="transport-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M5.2 5.5h11.2V2.8L21 7.1l-4.6 4.3V8.7H6.2c-1.3 0-2.3 1-2.3 2.3v1H.8v-1c0-3 2-5.5 4.4-5.5Z"
          fill="currentColor"
        />
        <path
          d="M18.8 18.5H7.6v2.7L3 16.9l4.6-4.3v2.7h10.2c1.3 0 2.3-1 2.3-2.3v-1h3.1v1c0 3-2 5.5-4.4 5.5Z"
          fill="currentColor"
        />
      </svg>
    )
  }
  return (
    <svg className="transport-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 3.8 20 12 6 20.2Z" fill="currentColor" />
    </svg>
  )
}

// Speaker glyphs of the mute toggle (handoff v0.8.3.1 transportIcon
// speaker-muted/low/medium/high), verbatim paths.
const SpeakerIcon = ({
  name,
}: {
  name: "speaker-muted" | "speaker-low" | "speaker-medium" | "speaker-high"
}): ReactElement => {
  const cone = <path d="M3.5 9v6h4L13 19.5v-15L7.5 9Z" fill="currentColor" />
  if (name === "speaker-muted") {
    return (
      <svg className="transport-icon volume-speaker-icon" viewBox="0 0 24 24" aria-hidden="true">
        {cone}
        <path d="m16 9 5 6m0-6-5 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    )
  }
  const waves =
    name === "speaker-low"
      ? "M16 9.2c1.5 1.6 1.5 4 0 5.6"
      : name === "speaker-medium"
      ? "M16 9.2c1.5 1.6 1.5 4 0 5.6M18.7 6.7c3 3 3 7.6 0 10.6"
      : "M16 9.2c1.5 1.6 1.5 4 0 5.6M18.7 6.7c3 3 3 7.6 0 10.6M21.2 4.2c4.2 4.2 4.2 11.4 0 15.6"
  return (
    <svg className="transport-icon volume-speaker-icon" viewBox="0 0 28 24" aria-hidden="true">
      {cone}
      <path d={waves} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

// Upstream bindReliableTransportButton: pointer input acts on pointerup; a
// keyboard-generated click (detail 0) stays available for Space/Enter.
const reliableTransport = (action: () => void | Promise<void>) => ({
  onPointerUp: (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    void action()
  },
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.detail !== 0) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    void action()
  },
})

type ResearchTab = "frequency" | "triage" | "audit"

// The Research panel (Frequency / Murmur Triage / Audit tabs) is excluded from
// the product: its murmur-triage sklearn model is research-only and
// version-fragile with no portable export, so the worker always reports triage
// as unavailable. The panel, its cards, and the researchOn plumbing stay in
// place so it can return if that changes; this flag only controls whether the
// toggle is rendered.
const RESEARCH_PANEL_ENABLED = false

/**
 * Control tips ("Tips On / Tips Off", upstream v0.8.3.18): the brief
 * explanations shown over buttons and controls. On by default and remembered
 * per browser under this key; where storage is unavailable the choice lasts
 * for the visit. Waveform data is a separate behaviour: it follows the
 * selected Visual guides, whatever this preference says.
 */
export const TOOLTIP_PREFERENCE_KEY = "medaica-pcg-tooltips"
export const readTooltipPreference = (): boolean => {
  try {
    return window.localStorage.getItem(TOOLTIP_PREFERENCE_KEY) !== "off"
  } catch {
    return true
  }
}
const writeTooltipPreference = (enabled: boolean): void => {
  try {
    window.localStorage.setItem(TOOLTIP_PREFERENCE_KEY, enabled ? "on" : "off")
  } catch {
    // Not remembered; the toggle still applies
  }
}

/**
 * The Guides cluster's control tips (upstream v0.9.1 data-hover text for a
 * viewer without an Evaluation Range). The toggles are upstream's divs (the
 * Energy one wraps the band controls, so it cannot be a button); they carry
 * the switch role for their state.
 */
const LAYER_TIPS: Record<"s1s2" | "regions" | "murmur", string> = {
  s1s2: "Show detected first and second heart sounds as review guides.",
  regions: "Show systole and diastole timing guides for review.",
  murmur: "Show or hide the Energy overlay. Energy displays repeatable acoustic activity by cardiac phase.",
}
/** The S1/S2 and Sys/Dia tips when the timing engine selected an Evaluation Range (upstream v0.9.1). */
const EVALUATION_RANGE_LAYER_TIPS: Record<"s1s2" | "regions", string> = {
  s1s2: "Show detected first and second heart sounds. Brighter markers are used for the results.",
  regions: "Show systole and diastole timing guides. Brighter guides are used for the results.",
}

// Enter / Space activate a non-button toggle, as a button would
const activateOnKey = (event: React.KeyboardEvent<HTMLElement>): void => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault()
    event.currentTarget.click()
  }
}

const PcgRecordingViewer = ({
  payload,
  turbulentModel = null,
  audioSrc = null,
  recordingId = null,
  examTimingConsistency = null,
  onExportPackage,
  onEnergyBandChange,
  onAudioGraph,
  onTooltipsEnabledChange,
  className = "",
  brandContainer = null,
  recordingHeader,
  recordingLabel,
  recordingHeaderActions,
  filterControls,
  reviewControls,
  secondaryActions,
  reviewSummary,
}: PcgRecordingViewerProps): ReactElement => {
  // ----- React-owned chrome state (discrete, low-frequency) -----
  const [layers, setLayers] = useState({ murmur: false, s1s2: false, regions: false, noise: false })
  // Coherent Energy sweep band: null = one-click preselection (the strongest
  // qualified band, resolved before Energy is enabled so the range label
  // advertises the band that will actually be shown). A string means the
  // reviewer moved the sweep (or clicked the reset-to-100 label).
  const [selectedEnergyBand, setSelectedEnergyBand] = useState<string | null>(null)
  // Marker-overlap check (research-only display overlay; requires Energy on).
  // Its Overlap control is not shown in this presentation (upstream v0.9 hides
  // `#markerOverlapPreview`), so the preview stays off; the state remains so
  // the canvas layer's research overlay keeps its wiring.
  const [markerOverlapPreview, setMarkerOverlapPreview] = useState(false)
  const [waveformView, setWaveformView] = useState<"clean" | "raw">("clean")
  const [researchOn, setResearchOn] = useState(false)
  const [researchTab, setResearchTab] = useState<ResearchTab>("frequency")
  const [cwtBand, setCwtBand] = useState("full")
  const [tooltipsEnabled, setTooltipsEnabled] = useState<boolean>(readTooltipPreference)
  useEffect(() => {
    onTooltipsEnabledChange?.(tooltipsEnabled)
  }, [onTooltipsEnabledChange, tooltipsEnabled])
  const [helpOpen, setHelpOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  // Brand menu (upstream v0.9.1 `viewerBrandMenu`): Quick Start and About.
  const [brandMenuOpen, setBrandMenuOpen] = useState(false)
  const brandMenuId = useId()
  const [cwtTitle, setCwtTitle] = useState("CWT — Full band")
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  // Listening gain (0–5×, ×1 at the slider midpoint — handoff v0.8.3.1).
  // Playback only: drives the graph's amplification node, never analysis.
  const [gainSlider, setGainSlider] = useState<number>(DEFAULT_GAIN_SLIDER_VALUE)
  const gainSliderRef = useRef(gainSlider)
  gainSliderRef.current = gainSlider
  // Loop/transport chrome is derived from the mutable viewer state; the loop
  // controller asks for a re-render (updateControls) after it changes it.
  const [, refreshChrome] = useReducer((n: number) => n + 1, 0)

  // ----- refs (canvases, audio, imperative chrome) -----
  const rootRef = useRef<HTMLDivElement | null>(null)
  const waveRef = useRef<HTMLCanvasElement | null>(null)
  const rulerRef = useRef<HTMLButtonElement | null>(null)
  const specRef = useRef<HTMLCanvasElement | null>(null)
  const tipRef = useRef<HTMLDivElement | null>(null)
  const smartHoverRef = useRef<HTMLDivElement | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const audioGraphRef = useRef<ReturnType<typeof createAudioGraph> | null>(null)
  const winLabelRef = useRef<HTMLSpanElement | null>(null)
  const audioTimeRef = useRef<HTMLSpanElement | null>(null)
  const zoomSliderRef = useRef<HTMLInputElement | null>(null)
  const playbackRafRef = useRef<number | null>(null)
  const hoveredRef = useRef(false)
  const brandButtonRef = useRef<HTMLButtonElement | null>(null)
  const brandMenuRef = useRef<HTMLDivElement | null>(null)

  const gate = rejectGate(payload)
  const gateRejected = isGateHardRejected(gate)
  // core-viewer.js syncLayerAvailability: Clean view is unavailable for rejected inputs.
  const effectiveWaveformView = gateRejected && waveformView === "clean" ? "raw" : waveformView

  // ----- analysis context: immutable, rebuilt on input change -----
  const core = useMemo(
    () =>
      createPcgCore(payload, {
        turbulentModel,
        examTimingConsistency,
        currentRecordingId: recordingId,
      }),
    [payload, turbulentModel, examTimingConsistency, recordingId]
  )

  // The timing engine's state for this guide (none / ready / unavailable).
  const engine = core.ctx.engine
  const hasEvaluationRange = engine.status === ENGINE_READY
  // The guide's engine state, from the payload alone: the per-recording view
  // and playback start must not reset when other core inputs change.
  const payloadEngine = useMemo(() => evaluationRangeEngine(payload), [payload])

  const traces = useMemo(() => decodeCanvasTraces(payload), [payload])
  // A new view per recording; with an Evaluation Range it opens framed on the
  // range (upstream __mvp2FrameEvalRange, once per recording).
  const view = useMemo(() => {
    const next = createView(payload.duration)
    const frame = evaluationRangeFrame(payloadEngine.selected, payload.duration)
    if (frame) {
      next.width = frame.width
      next.start = frame.start
      clampView(next, payload.duration)
    }
    return next
  }, [payload, payloadEngine])
  const stateRef = useRef(createViewerState())

  // ----- Coherent Energy band selection (research-only) -----
  // applyViewerPackage parity: a new payload resets the sweep to the one-click
  // preselection and drops the overlap preview.
  useEffect(() => {
    setSelectedEnergyBand(null)
    setMarkerOverlapPreview(false)
  }, [payload])
  // Upstream's engine bootstrap turns the S1/S2 guides on whenever the engine
  // ran for the recording.
  useEffect(() => {
    if (payloadEngine.injected) setLayers((prev) => (prev.s1s2 ? prev : { ...prev, s1s2: true }))
  }, [payloadEngine])
  // initializeRecommendedEnergyBand: resolve the review-start band up front so
  // the range label advertises the band that will actually be shown while the
  // Energy layer is still off (upstream bootstrap calls updateEnergyBandControls).
  const recommendedBand = useMemo(() => core.recommendedEnergyBand(), [core])
  const energyBand = selectedEnergyBand ?? recommendedBand
  // Prototype initializeRecommendedEnergyBand: auto-selected only while no
  // manual sweep happened and the preselection differs from the 100-300 Hz
  // reference.
  const energyBandAutoSelected = selectedEnergyBand === null && recommendedBand !== "s100"
  const energyBands = core.energyBandMap()
  const coherentResult = core.coherentEnergyV1(energyBand)
  // Report the active band so host pages can drive their own research surfaces.
  const onEnergyBandChangeRef = useRef(onEnergyBandChange)
  onEnergyBandChangeRef.current = onEnergyBandChange
  useEffect(() => {
    if (onEnergyBandChangeRef.current) {
      onEnergyBandChangeRef.current(energyBand, { autoSelected: energyBandAutoSelected })
    }
  }, [energyBand, energyBandAutoSelected])
  // setEnergySweep (upstream): snap to the 5 Hz grid, select the band, and make
  // sure the Energy layer is on — the sweep IS the research reveal.
  const setEnergySweep = (lowHz: number | string) => {
    const key = energySweepKey(lowHz)
    if (!energyBands[key]) return
    setSelectedEnergyBand(key)
    if (!gateRejected) setLayers((prev) => (prev.murmur ? prev : { ...prev, murmur: true }))
  }

  const getPlaybackTime = useCallback(() => {
    // core-viewer.js currentPlaybackTime, verbatim.
    const audio = audioRef.current
    if (!audio || !Number.isFinite(audio.currentTime)) return null
    return Math.max(0, Math.min(payload.duration || 0, audio.currentTime))
  }, [payload])

  // The canvas draws the visual scope (with the engine, the whole recording's
  // markers); its analysis inputs come from the core (the metric scope).
  const rc = useMemo(
    () =>
      createRenderContext({
        payload: core.ctx.visualPayload,
        core,
        traces,
        view,
        state: stateRef.current,
        canvases: {},
        getPlaybackTime,
        chrome: { setCwtTitle },
      }),
    [core, traces, view, getPlaybackTime]
  )

  // Mirror React chrome state into the mutable state object the verbatim draw
  // code reads. Mutating our own ref during render is safe and keeps the draw
  // path synchronous with the committed UI.
  const energyActive = layers.murmur && !gateRejected
  stateRef.current.layers = layers
  stateRef.current.waveformView = effectiveWaveformView
  stateRef.current.researchTab = researchTab
  stateRef.current.cwtBand = cwtBand
  stateRef.current.tooltipsEnabled = tooltipsEnabled
  stateRef.current.energyGuideMode = "coherent"
  stateRef.current.energyBand = energyBand
  stateRef.current.energyBandInitialized = true
  stateRef.current.energyBandAutoSelected = energyBandAutoSelected
  // updateEnergyBandControls: the overlap preview cannot outlive the Energy layer.
  stateRef.current.markerOverlapPreview = markerOverlapPreview && energyActive

  // ----- imperative draw schedule (the prototype's redraw()) -----
  const redraw = useCallback(() => {
    rc.canvases.wave = waveRef.current
    rc.canvases.minimap = null
    rc.canvases.spec = researchOn && researchTab === "frequency" ? specRef.current : null
    drawWave(rc)
    if (rc.canvases.spec) drawSpec(rc)
    // updateWinLabel / zoom-window slider sync, imperative.
    if (winLabelRef.current) winLabelRef.current.textContent = rc.view.width.toFixed(1) + " s"
    const zoomSlider = zoomSliderRef.current
    if (zoomSlider) {
      const minWidth = Math.min(1.5, payload.duration)
      const maxWidth = Math.max(minWidth, payload.duration)
      const ratio = maxWidth > minWidth ? Math.log(rc.view.width / minWidth) / Math.log(maxWidth / minWidth) : 1
      zoomSlider.value = String(Math.round(100 * Math.max(0, Math.min(1, ratio))))
      zoomSlider.disabled = maxWidth <= minWidth + 0.01
      zoomSlider.setAttribute("aria-valuetext", `${rc.view.width.toFixed(1)} seconds visible`)
    }
  }, [rc, researchOn, researchTab, payload])

  const updateAudioTime = useCallback(() => {
    const audio = audioRef.current
    if (!audioTimeRef.current) return
    const duration = audio && Number.isFinite(audio.duration) ? audio.duration : payload.duration || 0
    audioTimeRef.current.textContent = `${formatAudioTime(audio ? audio.currentTime : 0)} / ${formatAudioTime(
      duration
    )}`
  }, [payload])

  // core-viewer.js seekPlayback, verbatim modulo injected redraw. Outside Loop
  // mode, keep the listening position inside the visible PCG; loop-boundary
  // seeks opt out because that viewport is authoritative.
  const seekPlayback = useCallback(
    (t: number, opts: { keepVisible?: boolean } = {}) => {
      const audio = audioRef.current
      if (!audio?.src || !payload.duration) return false
      const next = Math.max(0, Math.min(payload.duration, t))
      try {
        audio.currentTime = next
      } catch (err) {
        return false
      }
      if (opts.keepVisible !== false && !rc.state.windowLoop) ensurePlaybackVisible(rc.view, payload.duration, next)
      redraw()
      updateAudioTime()
      return true
    },
    [payload, rc, redraw, updateAudioTime]
  )

  // startAudioPlayback (upstream): a MediaElement routed through a suspended
  // AudioContext can report `playing` while its clock remains frozen, so resume
  // the graph before asking the media element to advance.
  const startAudioPlayback = useCallback(async (audio: HTMLAudioElement | null = audioRef.current) => {
    if (!audio?.src) return false
    if (audioGraphRef.current) audioGraphRef.current.resume()
    try {
      await audio.play()
      return true
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Playback failed", err)
      return false
    }
  }, [])

  // ----- Loop controller: one per render context, disposed with it -----
  // The controller reads the wrapper's latest seek/redraw through refs so the
  // verbatim loop logic never closes over a stale callback identity.
  const seekPlaybackRef = useRef(seekPlayback)
  seekPlaybackRef.current = seekPlayback
  const redrawRef = useRef(redraw)
  redrawRef.current = redraw
  const loop = useMemo(
    () =>
      createLoopController(rc, {
        getAudio: () => audioRef.current,
        seekPlayback: (t: number, opts?: { keepVisible?: boolean }) => seekPlaybackRef.current(t, opts),
        redraw: () => redrawRef.current(),
        startAudioPlayback,
        updateControls: () => refreshChrome(),
        getMasterGain: () => (audioGraphRef.current ? audioGraphRef.current.output : null),
      }),
    [rc, startAudioPlayback]
  )
  useEffect(() => () => loop.dispose(), [loop])
  const windowLoop = Boolean(rc.state.windowLoop)

  // ----- blob URL lifecycle -----
  const audioUrl = useMemo(() => {
    if (audioSrc && typeof Blob !== "undefined" && audioSrc instanceof Blob) return URL.createObjectURL(audioSrc)
    return typeof audioSrc === "string" ? audioSrc : ""
  }, [audioSrc])
  useEffect(() => {
    return () => {
      // The prototype revoked a replaced blob URL in syncAudio() but leaked the
      // final one on navigation; here every created URL is revoked.
      if (audioUrl?.startsWith("blob:")) URL.revokeObjectURL(audioUrl)
    }
  }, [audioUrl])
  // syncAudio(): a new audio source drops any loop.
  useEffect(() => {
    loop.reset()
    refreshChrome()
  }, [audioUrl, loop])

  // ----- Web Audio lifecycle: once per mounted audio element -----
  // onAudioGraph goes through a ref so a new callback identity can never re-run
  // this effect: createMediaElementSource is one-shot per element, so the graph
  // MUST be created exactly once per mounted <audio>.
  const onAudioGraphRef = useRef(onAudioGraph)
  onAudioGraphRef.current = onAudioGraph
  useEffect(() => {
    const graph = createAudioGraph(audioRef.current)
    audioGraphRef.current = graph
    // A fresh graph starts at unity; re-apply the current listening gain.
    if (graph) graph.setAmplification(playbackGainFromSlider(gainSliderRef.current))
    if (onAudioGraphRef.current) onAudioGraphRef.current(graph)
    return () => {
      if (onAudioGraphRef.current) onAudioGraphRef.current(null)
      if (graph) graph.close()
      audioGraphRef.current = null
    }
  }, [])

  // Playback starts at the Evaluation Range, once per recording (upstream
  // __mvp2SeekEvalStartOnce). The position is set when the recording's audio
  // has loaded its metadata: on a recording change the previous recording's
  // audio is still loaded until the host hands over the new one, so the seek
  // waits for the next load rather than moving the old audio.
  const evalRangeStartTime = useMemo(
    () => evaluationRangeStart(payloadEngine.selected, payload.duration),
    [payloadEngine, payload]
  )
  const evalStartSoughtRef = useRef<unknown>(null)
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || evalRangeStartTime === null) return undefined
    const seekToRangeStart = () => {
      if (evalStartSoughtRef.current === payload) return
      try {
        audio.currentTime = evalRangeStartTime
        evalStartSoughtRef.current = payload
      } catch (err) {
        // Not seekable; the next load tries again
      }
      updateAudioTime()
    }
    audio.addEventListener("loadedmetadata", seekToRangeStart)
    return () => audio.removeEventListener("loadedmetadata", seekToRangeStart)
  }, [payload, evalRangeStartTime, updateAudioTime])

  // The recording's playback-filter level-compensation gains follow the guide
  // payload (upstream v0.9.1 `playbackFilterLoudness`); guides without the
  // key resolve to the fallback table. Playback only, never analysis.
  useEffect(() => {
    if (audioGraphRef.current) audioGraphRef.current.setFilterLevelGains(playbackFilterLevelGains(payload))
  }, [payload])

  // ----- brand menu: outside click and Escape close it, focus returns to the trigger -----
  const closeBrandMenu = useCallback((focusTrigger: boolean) => {
    setBrandMenuOpen(false)
    if (focusTrigger) brandButtonRef.current?.focus()
  }, [])
  useEffect(() => {
    if (!brandMenuOpen) return undefined
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (brandMenuRef.current?.contains(target) || brandButtonRef.current?.contains(target)) return
      closeBrandMenu(false)
    }
    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeBrandMenu(true)
    }
    document.addEventListener("click", onDocumentClick)
    document.addEventListener("keydown", onDocumentKeyDown)
    return () => {
      document.removeEventListener("click", onDocumentClick)
      document.removeEventListener("keydown", onDocumentKeyDown)
    }
  }, [brandMenuOpen, closeBrandMenu])
  // A menu choice opens its dialog; focus goes back to the trigger first so
  // the dialog returns it there on close.
  const chooseBrandMenuItem = (open: (value: boolean) => void) => {
    closeBrandMenu(true)
    open(true)
  }

  // ----- playback listeners + rAF loop -----
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return undefined

    // playbackRenderTick/start/stopPlaybackRender; the loop controller owns
    // the boundary/follow branch.
    const tick = () => {
      if (!audio || audio.paused || audio.ended) {
        playbackRafRef.current = null
        drawWave(rc)
        return
      }
      loop.playbackTick(audio)
      drawWave(rc)
      updateAudioTime()
      playbackRafRef.current = requestAnimationFrame(tick)
    }
    const startPlaybackRender = () => {
      if (playbackRafRef.current) cancelAnimationFrame(playbackRafRef.current)
      playbackRafRef.current = requestAnimationFrame(tick)
    }
    const stopPlaybackRender = () => {
      if (playbackRafRef.current) cancelAnimationFrame(playbackRafRef.current)
      playbackRafRef.current = null
    }

    const onPlay = () => {
      // resume a suspended AudioContext on play (autoplay policy).
      if (audioGraphRef.current) audioGraphRef.current.resume()
      startPlaybackRender()
      setIsPlaying(true)
      updateAudioTime()
    }
    const onPlaying = () => {
      startPlaybackRender()
      setIsPlaying(true)
    }
    const onPause = () => {
      loop.handlePause()
      stopPlaybackRender()
      setIsPlaying(false)
      updateAudioTime()
      redraw()
    }
    const onEnded = () => {
      if (loop.handleEnded()) return
      stopPlaybackRender()
      setIsPlaying(false)
      updateAudioTime()
      redraw()
    }
    const onSeeked = () => {
      const t = getPlaybackTime()
      if (!loop.handleSeeked(t)) return
      updateAudioTime()
      redraw()
    }
    const onTimeUpdate = () => {
      loop.handleTimeUpdate(audio.currentTime)
      updateAudioTime()
      drawWave(rc)
    }
    const onMeta = () => updateAudioTime()

    audio.addEventListener("play", onPlay)
    audio.addEventListener("playing", onPlaying)
    audio.addEventListener("pause", onPause)
    audio.addEventListener("ended", onEnded)
    audio.addEventListener("seeked", onSeeked)
    audio.addEventListener("timeupdate", onTimeUpdate)
    audio.addEventListener("loadedmetadata", onMeta)
    audio.addEventListener("durationchange", onMeta)
    return () => {
      audio.removeEventListener("play", onPlay)
      audio.removeEventListener("playing", onPlaying)
      audio.removeEventListener("pause", onPause)
      audio.removeEventListener("ended", onEnded)
      audio.removeEventListener("seeked", onSeeked)
      audio.removeEventListener("timeupdate", onTimeUpdate)
      audio.removeEventListener("loadedmetadata", onMeta)
      audio.removeEventListener("durationchange", onMeta)
      stopPlaybackRender()
    }
  }, [rc, loop, payload, redraw, updateAudioTime, getPlaybackTime])

  // ----- canvas interactions + window listeners -----
  useEffect(() => {
    const waveCanvas = waveRef.current
    const detachers: Array<() => void> = []
    if (waveCanvas) {
      const panZoom = attachWavePanZoom(rc, waveCanvas, tipRef.current, {
        seekPlayback,
        redraw,
        getAudio: () => audioRef.current,
        loop,
      })
      detachers.push(() => panZoom.detach())
      if (tipRef.current) {
        detachers.push(attachWaveTooltip(rc, waveCanvas, tipRef.current, { isPanning: panZoom.isPanning }))
      }
      if (rulerRef.current) {
        const ruler = attachLoopRuler(rc, rulerRef.current, waveCanvas, loop, {
          getAudio: () => audioRef.current,
          seekPlayback,
          startAudioPlayback,
        })
        detachers.push(() => ruler.detach())
      }
    }
    detachers.push(
      attachKeyboardShortcuts(rc, {
        redraw,
        // Component-scoped: pointer over the viewer, or focus inside it.
        isActive: () =>
          hoveredRef.current ||
          Boolean(rootRef.current && document.activeElement && rootRef.current.contains(document.activeElement)),
      })
    )
    const onWindowResize = () => redraw()
    const hideSmartHover = () => {
      if (smartHoverRef.current) smartHoverRef.current.style.display = "none"
    }
    window.addEventListener("resize", onWindowResize)
    window.addEventListener("scroll", hideSmartHover, { passive: true })
    detachers.push(() => {
      window.removeEventListener("resize", onWindowResize)
      window.removeEventListener("scroll", hideSmartHover)
    })
    redraw()
    return () => {
      detachers.forEach((detach) => detach())
    }
  }, [rc, loop, redraw, seekPlayback, startAudioPlayback])

  // Redraw when chrome state changes (layer toggles, view mode, research panel,
  // Coherent Energy sweep band, overlap preview).
  useEffect(() => {
    redraw()
  }, [redraw, layers, effectiveWaveformView, cwtBand, researchOn, researchTab, energyBand, markerOverlapPreview])

  // ----- smart hover (data-hover) — root-scoped, not document-scoped -----
  // core-viewer.js smart hover, with document listeners replaced by React
  // handlers on the component root (auto-removed with the component).
  const positionSmartHover = useCallback(
    (target: Element) => {
      const smartHover = smartHoverRef.current
      if (!smartHover) return
      const text = (target as HTMLElement).dataset.hover
      if (!text || !tooltipsEnabled) {
        smartHover.style.display = "none"
        return
      }
      smartHover.textContent = text
      smartHover.style.display = "block"
      smartHover.style.left = "0px"
      smartHover.style.top = "0px"
      const pad = 12
      const gap = 8
      const rect = target.getBoundingClientRect()
      const tipRect = smartHover.getBoundingClientRect()
      const minX = pad
      const maxX = Math.max(minX, window.innerWidth - tipRect.width - pad)
      let x = rect.left + rect.width / 2 - tipRect.width / 2
      x = Math.max(minX, Math.min(maxX, x))
      let y = rect.top - tipRect.height - gap
      if (y < pad) y = rect.bottom + gap
      const maxY = Math.max(pad, window.innerHeight - tipRect.height - pad)
      y = Math.max(pad, Math.min(maxY, y))
      smartHover.style.left = `${Math.round(x)}px`
      smartHover.style.top = `${Math.round(y)}px`
    },
    [tooltipsEnabled]
  )
  const hideSmartHover = useCallback(() => {
    if (smartHoverRef.current) smartHoverRef.current.style.display = "none"
  }, [])
  const onRootMouseOver = useCallback(
    (e: React.MouseEvent) => {
      const target = (e.target as Element).closest?.(".hover-help, .control-help")
      if (target) positionSmartHover(target)
    },
    [positionSmartHover]
  )
  const onRootMouseOut = useCallback(
    (e: React.MouseEvent) => {
      const target = (e.target as Element).closest?.(".hover-help, .control-help")
      if (target && (!e.relatedTarget || !target.contains(e.relatedTarget as Node))) hideSmartHover()
    },
    [hideSmartHover]
  )
  // Keyboard focus shows a control's tip as hovering does (upstream focusin / focusout)
  const onRootFocus = useCallback(
    (e: React.FocusEvent) => {
      const target = (e.target as Element).closest?.(".hover-help, .control-help")
      if (target) positionSmartHover(target)
    },
    [positionSmartHover]
  )
  const onRootBlur = useCallback(
    (e: React.FocusEvent) => {
      const target = (e.target as Element).closest?.(".hover-help, .control-help")
      if (target) hideSmartHover()
    },
    [hideSmartHover]
  )
  const toggleTooltips = () => {
    const next = !tooltipsEnabled
    writeTooltipPreference(next)
    setTooltipsEnabled(next)
    if (!next) hideSmartHover()
  }

  // ----- chrome actions -----
  const toggleLayer = (k: keyof typeof layers) => {
    if (gateRejected) return // syncLayerAvailability: layers disabled on reject
    setLayers((prev) => ({ ...prev, [k]: !prev[k] }))
    // Turning Energy off also ends the overlap preview.
    if (k === "murmur" && layers.murmur) setMarkerOverlapPreview(false)
  }
  // Transport (handoff v0.8.3): Start means the beginning of the recording in
  // every mode, Play/Pause never rewrites the viewport, Loop arms the visible
  // window without starting playback.
  const transportStart = async () => {
    const audio = audioRef.current
    if (!audio?.src) return
    loop.disableLoop()
    seekPlayback(0, { keepVisible: true })
    await startAudioPlayback(audio)
    refreshChrome()
  }
  const transportPlayToggle = async () => {
    const audio = audioRef.current
    if (!audio?.src) return
    if (audio.paused) {
      await startAudioPlayback(audio)
    } else {
      loop.prepareManualPause()
      audio.pause()
    }
    refreshChrome()
  }
  const transportLoopToggle = async () => {
    await loop.toggleVisibleWindowLoop()
  }
  const exportPackage = () => {
    if (!onExportPackage) return
    onExportPackage(core.buildCardiacReportEvidencePackage({ recordingId: recordingId || undefined }))
  }

  // updateEnergyBandControls (upstream): every control state derived per
  // render from the selected band's coherent/overlap results.
  const energyRange = coherentResult.rangeHz || [100, 300]
  const energyRecurrence = Math.max(coherentResult.sysRecurrencePct || 0, coherentResult.diaRecurrencePct || 0)
  const energyControlsDisabled = !energyActive || !energyBands[energyBand]
  // Upstream f92d3be: the sweep is offered only when the payload carries more
  // than one Energy band (research payloads); the released single 100-300 Hz
  // band is shown as a fixed readout instead of an inoperative slider.
  const energyAdjustable = Object.keys(energyBands).length > 1
  const energySweepTitle =
    `${energyRange.join("-")} Hz${energyBandAutoSelected ? " · suggested review-start band" : ""} · ` +
    `${String(coherentResult.status).replace(/_/g, " ")} · peak recurrence ${energyRecurrence.toFixed(0)}% · ` +
    `${coherentResult.reason} · Click to reset to 100-300 Hz.`
  const energySweepButtonClasses = [
    energyActive ? "selected" : "",
    coherentResult.status === "phase_coherent" ? "available" : "",
    coherentResult.status === "phase_coherent" && energyRecurrence >= 60 ? "strong" : "",
  ]
    .filter(Boolean)
    .join(" ")
  // Clean / Raw: the control tip doubles as the accessible name
  const waveformViewTip =
    effectiveWaveformView === "clean"
      ? "Switch between the Clean visual PCG trace and Raw source trace. Playback, calculations, guide timing, and export are unchanged."
      : gateRejected
      ? "Clean PCG view is unavailable for rejected inputs. Raw source view remains available."
      : "Switch to Clean visual PCG view. Audio, analysis, guide timing, and export are unchanged."
  // A click on the sweep popover must never reach the Energy toggle and switch
  // the layer off (upstream stops pointerdown/pointerup/click/change over the
  // popover). The toggle ignores clicks from inside it instead of the popover
  // carrying click handlers of its own. The fixed readout lets clicks
  // through, so clicking it toggles Energy like the rest of the control.
  const onEnergyToggleClick = (e: React.MouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest(".energy-sweep-popover")) return
    toggleLayer("murmur")
  }

  const nativeKhz = payload.sr_native ? `${fmtNumber(payload.sr_native / 1000, 1)}kHz` : ""
  const rootClasses = [
    "pcg-viewer",
    researchOn ? "research" : "",
    tooltipsEnabled ? "" : "control-tooltips-off",
    className,
  ]
    .filter(Boolean)
    .join(" ")
  const loopTitle = windowLoop ? "Loop on — click to turn off" : "Loop visible window"
  const rulerTitle = windowLoop
    ? "Drag the loop bar to move it; drag an edge to resize"
    : "Click to loop the visible window"

  // The brand: the logo and the "Viewer" menu button (upstream v0.9.1 header).
  const brand = (
    <div className="exam-brand">
      <MedaicaLogo className="exam-brand-logo" aria-hidden="true" focusable="false" />
      <button
        ref={brandButtonRef}
        className="exam-brand-name"
        type="button"
        title="Viewer menu"
        aria-haspopup="menu"
        aria-expanded={brandMenuOpen}
        aria-controls={brandMenuOpen ? brandMenuId : undefined}
        onClick={() => setBrandMenuOpen((open) => !open)}
      >
        Viewer
      </button>
      {brandMenuOpen ? (
        <div ref={brandMenuRef} id={brandMenuId} className="exam-brand-menu" role="menu" aria-label="Viewer menu">
          <button type="button" role="menuitem" onClick={() => chooseBrandMenuItem(setHelpOpen)}>
            Quick Start
          </button>
          <button type="button" role="menuitem" onClick={() => chooseBrandMenuItem(setAboutOpen)}>
            About
          </button>
        </div>
      ) : null}
    </div>
  )

  return (
    <div
      ref={rootRef}
      className={rootClasses}
      tabIndex={0}
      onMouseEnter={() => {
        hoveredRef.current = true
      }}
      onMouseLeave={() => {
        hoveredRef.current = false
        if (smartHoverRef.current) smartHoverRef.current.style.display = "none"
      }}
      onMouseOver={onRootMouseOver}
      onMouseOut={onRootMouseOut}
      onFocus={onRootFocus}
      onBlur={onRootBlur}
    >
      <div className="smart-hover-tooltip" ref={smartHoverRef} />

      <div className="panel">
        {/* Selected-recording head: the brand menu, host navigation, then the
            Tips On / Off preference, the host's controls (the interface-size
            toggle) and Quick Start — the handoff viewer's order. */}
        <div className="selected-recording-head">
          {brandContainer ? createPortal(brand, brandContainer) : brand}
          {recordingHeader}
          <button
            className={`zoom-btn preference-toggle control-help${tooltipsEnabled ? " on" : " off"}`}
            type="button"
            aria-pressed={tooltipsEnabled}
            aria-label={tooltipsEnabled ? "Turn control tips off" : "Turn control tips on"}
            data-hover="Turn brief explanations for controls on or off. Waveform data for selected Visual guides is unaffected."
            onClick={toggleTooltips}
          >
            <span className="preference-led" aria-hidden="true" />
            <span>{tooltipsEnabled ? "Tips On" : "Tips Off"}</span>
          </button>
          {recordingHeaderActions}
          <button
            className="pcg-help-btn control-help"
            type="button"
            aria-haspopup="dialog"
            aria-expanded={helpOpen}
            data-hover="Open a concise guide to reviewing recordings and choosing evidence for Medaica Report."
            onClick={() => setHelpOpen(true)}
          >
            Quick Start
          </button>
        </div>
        <PcgViewerHelpDialog
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          hasEvaluationRange={hasEvaluationRange}
        />
        <PcgViewerAboutDialog
          open={aboutOpen}
          onClose={() => setAboutOpen(false)}
          hasEvaluationRange={hasEvaluationRange}
        />

        <div className="wave-plot">
          <button
            ref={rulerRef}
            type="button"
            className={`wave-loop-ruler${windowLoop ? " loop-on" : ""}`}
            title={rulerTitle}
            aria-label="Loop visible window"
            aria-pressed={windowLoop}
          />
          <canvas ref={waveRef} className="pcg-wave" height={350} />
          <div className="tooltip" ref={tipRef} />
        </div>
        <PcgEvaluationRangeStrip core={core} />

        <div className="audio-row">
          <div className="audio-primary-controls">
            <span className="control-row-label">Audio</span>
            <div className="audio-playback-block" aria-label="Audio playback">
              <div className="compact-audio" aria-label="Playback controls">
                <button
                  className="compact-audio-btn transport-btn"
                  type="button"
                  title="Play from beginning"
                  aria-label="Play from beginning"
                  {...reliableTransport(transportStart)}
                >
                  <TransportIcon name="start" />
                </button>
                <button
                  className="compact-audio-btn transport-btn"
                  type="button"
                  title={isPlaying ? "Pause" : "Play"}
                  aria-label={isPlaying ? "Pause" : "Play"}
                  {...reliableTransport(transportPlayToggle)}
                >
                  <TransportIcon name={isPlaying ? "pause" : "play"} />
                </button>
                <button
                  className={`compact-audio-btn transport-btn${windowLoop ? " loop-on" : ""}`}
                  type="button"
                  title={loopTitle}
                  aria-label="Loop visible window"
                  aria-pressed={windowLoop}
                  {...reliableTransport(transportLoopToggle)}
                >
                  <TransportIcon name="loop" />
                </button>
                <span className="compact-audio-time" ref={audioTimeRef}>
                  0:00 / 0:00
                </span>
                <button
                  className="compact-audio-btn compact-mute-toggle"
                  type="button"
                  title={isMuted ? "Unmute playback" : "Mute playback"}
                  aria-label={isMuted ? "Unmute playback" : "Mute playback"}
                  onClick={() => {
                    const audio = audioRef.current
                    if (!audio) return
                    audio.muted = !audio.muted
                    setIsMuted(audio.muted)
                  }}
                >
                  <SpeakerIcon name={speakerIconFor(isMuted ? 0 : playbackGainFromSlider(gainSlider))} />
                </button>
                <span className="compact-gain-label" aria-live="polite">
                  {formatPlaybackGain(playbackGainFromSlider(gainSlider))}
                </span>
                <input
                  className="compact-audio-volume"
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={gainSlider}
                  aria-label="Playback amplification"
                  title={
                    playbackGainFromSlider(gainSlider) === 0
                      ? "Audio off"
                      : `Playback amplification ${formatPlaybackGain(
                          playbackGainFromSlider(gainSlider)
                        )}. Playback only; calculated results are unchanged.`
                  }
                  onChange={(e) => {
                    const sliderValue = Number((e.target as HTMLInputElement).value)
                    setGainSlider(sliderValue)
                    const nextGain = playbackGainFromSlider(sliderValue)
                    const audio = audioRef.current
                    if (audio) {
                      // Element volume stays at 1 — amplification happens in the
                      // graph's dedicated gain node (0–5×); gain 0 mutes.
                      audio.volume = 1
                      audio.muted = nextGain === 0
                      setIsMuted(audio.muted)
                    }
                    if (audioGraphRef.current) audioGraphRef.current.setAmplification(nextGain)
                  }}
                />
              </div>
            </div>
            <audio ref={audioRef} className="pcg-audio" preload="metadata" src={audioUrl || undefined} />
            {filterControls}
          </div>
        </div>

        <div className="panel-header">
          <span className="control-row-label">Visual</span>
          <div className="waveform-controls">
            <div className="control-cluster" aria-label="Waveform view and guide layers">
              <span className="control-cluster-label">Guides</span>
              <button
                className={`zoom-btn on waveform-view-btn control-help${gateRejected ? " disabled" : ""}`}
                type="button"
                onClick={() => {
                  if (!gateRejected) setWaveformView(effectiveWaveformView === "clean" ? "raw" : "clean")
                }}
                aria-label={waveformViewTip}
                data-hover={waveformViewTip}
              >
                {effectiveWaveformView === "clean" ? "Clean" : "Raw"}
              </button>
              {(
                [
                  ["s1s2", "S1/S2", "#cbd5e1"],
                  ["regions", "Sys/Dia", "#94a3b8"],
                ] as Array<[keyof typeof layers, string, string]>
              ).map(([key, label, color]) => (
                <div
                  key={key}
                  className={`toggle ${layers[key] ? "on" : "off"} control-help${gateRejected ? " disabled" : ""}`}
                  data-layer={key}
                  role="switch"
                  tabIndex={0}
                  aria-checked={layers[key]}
                  data-hover={
                    hasEvaluationRange
                      ? EVALUATION_RANGE_LAYER_TIPS[key as "s1s2" | "regions"]
                      : LAYER_TIPS[key as "s1s2" | "regions"]
                  }
                  onClick={() => toggleLayer(key)}
                  onKeyDown={activateOnKey}
                >
                  <span className="dot" style={{ background: color }} />
                  <span className="label-text">{label}</span>
                </div>
              ))}
              <div
                className={`toggle ${layers.murmur ? "on" : "off"} control-help${gateRejected ? " disabled" : ""}${
                  energyAdjustable ? "" : " fixed-energy-band"
                }`}
                data-layer="murmur"
                role="switch"
                tabIndex={0}
                aria-checked={layers.murmur}
                data-hover={LAYER_TIPS.murmur}
                onClick={onEnergyToggleClick}
                onKeyDown={activateOnKey}
              >
                <span className="dot" style={{ background: "#ef4444" }} />
                <span className="label-text">Energy</span>
                <div
                  className={`energy-band-switch${energyAdjustable ? "" : " fixed-band"}`}
                  aria-label="Energy frequency band"
                >
                  {energyAdjustable ? (
                    <>
                      <button
                        type="button"
                        className={energySweepButtonClasses}
                        data-testid="pcg-energy-sweep-button"
                        disabled={energyControlsDisabled}
                        title={energySweepTitle}
                        onClick={(e) => {
                          e.stopPropagation()
                          setEnergySweep(100)
                        }}
                      >
                        <span data-testid="pcg-energy-sweep-label">{`${energyRange[0]}–${energyRange[1]} Hz`}</span>
                      </button>
                      <div className="energy-sweep-popover">
                        <input
                          className="energy-sweep-slider"
                          data-testid="pcg-energy-sweep-slider"
                          type="range"
                          min="35"
                          max="250"
                          step="5"
                          value={energyRange[0]}
                          disabled={energyControlsDisabled}
                          aria-label="Energy frequency window lower bound"
                          onChange={(e) => {
                            e.stopPropagation()
                            setEnergySweep(e.target.value)
                          }}
                        />
                      </div>
                    </>
                  ) : (
                    <span className="energy-fixed-label" data-testid="pcg-energy-fixed-label">
                      {`${energyRange[0]}–${energyRange[1]} Hz`}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="control-cluster" aria-label="Waveform zoom controls">
              <span className="control-cluster-label">Zoom</span>
              <span
                className="wave-meta"
                style={{ fontSize: 10, color: "#4b5563", cursor: "help" }}
                title={
                  payload.sr_proc
                    ? `${payload.duration.toFixed(1)}s @ ${payload.sr_native} Hz · guide processing ${
                        payload.sr_proc
                      } Hz`
                    : `${payload.duration.toFixed(1)}s @ ${payload.sr_native} Hz`
                }
              >
                {payload.duration.toFixed(1)}s{nativeKhz ? ` @ ${nativeKhz}` : ""}
              </span>
              <span
                ref={winLabelRef}
                data-testid="pcg-win-label"
                style={{
                  fontSize: 10,
                  color: "#cbd5e1",
                  minWidth: 42,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                }}
              />
              <input
                ref={zoomSliderRef}
                className="zoom-window-slider"
                data-testid="pcg-zoom-slider"
                type="range"
                min="0"
                max="100"
                step="1"
                defaultValue="50"
                aria-label="Visible waveform duration in seconds"
                onInput={(e) => {
                  // Log-scale zoom-window slider; a loop edit brackets the gesture.
                  loop.beginLoopViewportEdit()
                  const minWidth = Math.min(1.5, payload.duration)
                  const maxWidth = Math.max(minWidth, payload.duration)
                  const position = Math.max(0, Math.min(1, Number((e.target as HTMLInputElement).value) / 100))
                  const seconds = maxWidth > minWidth ? minWidth * Math.pow(maxWidth / minWidth, position) : maxWidth
                  setZoomCentered(rc.view, payload.duration, seconds)
                  redraw()
                  loop.scheduleViewportCommit(120)
                }}
              />
            </div>
          </div>
        </div>

        <PcgEvaluationRangeConfidence core={core} />
        <PcgPatternResultsCard core={core} recordingLabel={recordingLabel} />
        <PcgStatsBar core={core} />

        <div className="exam-review-row">
          <div className="exam-review-left">{reviewControls}</div>
          <div className="exam-secondary-actions">
            {secondaryActions}
            {onExportPackage ? (
              <button
                className="zoom-btn"
                type="button"
                title="Send reviewed results to Report"
                onClick={exportPackage}
              >
                Export to Report
              </button>
            ) : null}
            {RESEARCH_PANEL_ENABLED ? (
              <button
                className={`zoom-btn${researchOn ? " on" : ""}`}
                type="button"
                title="Show research views"
                aria-pressed={researchOn}
                onClick={() => setResearchOn((v) => !v)}
              >
                {researchOn ? "Research on" : "Research"}
              </button>
            ) : null}
          </div>
          {reviewSummary}
        </div>
      </div>

      {researchOn ? (
        <div className="panel research-panel" style={{ display: "block" }}>
          <div className="panel-header">
            <span className="panel-title">Research</span>
            <span className="research-subtitle">Opt-in frequency, triage, and audit views</span>
          </div>
          <div className="research-tabs" role="tablist" aria-label="Research views">
            {(
              [
                ["frequency", "Frequency"],
                ["triage", "Murmur Triage"],
                ["audit", "Audit"],
              ] as Array<[ResearchTab, string]>
            ).map(([tab, label]) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={researchTab === tab}
                className={`research-tab${researchTab === tab ? " on" : ""}`}
                onClick={() => setResearchTab(tab)}
              >
                {label}
              </button>
            ))}
          </div>
          {researchTab === "frequency" ? (
            <div className="research-section on">
              <div className="panel-header">
                <span className="panel-title">{cwtTitle}</span>
                <div className="research-controls" aria-label="CWT frequency band">
                  <span className="label">Band</span>
                  {Object.keys(CWT_BANDS).map((band) => (
                    <button
                      key={band}
                      className={`zoom-btn${cwtBand === band ? " on" : ""}`}
                      type="button"
                      onClick={() => setCwtBand(band)}
                    >
                      {(CWT_BANDS as any)[band].label}
                    </button>
                  ))}
                </div>
              </div>
              <canvas ref={specRef} className="pcg-spec" height={220} />
            </div>
          ) : null}
          {researchTab === "triage" ? (
            <div className="research-section on">
              <PcgTriagePanel core={core} energyBand={energyBand} />
            </div>
          ) : null}
          {researchTab === "audit" ? (
            <div className="research-section on">
              <PcgAuditPanel core={core} energyBand={energyBand} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default PcgRecordingViewer
